import {
    Button,
    DatePicker,
    Flex,
    Form,
    Popup,
    Spacing,
    Text,
    TimePicker,
    ToggleButton,
} from '@approvalmax/ui/src/components';
import { VisibilityOnIcon } from '@approvalmax/ui/src/icons';
import { memo, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { SchedulerDay } from './components/SchedulerDay/SchedulerDay';
import { SchedulerMonth } from './components/SchedulerMonth/SchedulerMonth';
import { SchedulerYear } from './components/SchedulerYear/SchedulerYear';
import { schedulerFormNames, ScheduleType } from './Scheduler.constants';
import { generateQuartzCronExpression } from './Scheduler.helpers';
import { useSchedulerPopup, useSchedulerType } from './Scheduler.hooks';
import { messages } from './Scheduler.messages';
import { SchedulerFormData } from './Scheduler.types';

export const SchedulerPopup = memo(() => {
    const { isOpen, handleClose } = useSchedulerPopup();
    const { schedulerType, setSchedulerType } = useSchedulerType();
    const form = useForm<Partial<SchedulerFormData>>();

    const handleTypeChange = useCallback(
        (value: string) => {
            setSchedulerType(value as ScheduleType);
        },
        [setSchedulerType]
    );

    const handleFormChange = useCallback((value: Partial<SchedulerFormData>) => {
        if (!value) {
            return;
        }

        // Generate the expression but don't store it since it's not used yet
        generateQuartzCronExpression(value);
    }, []);

    return (
        <Popup open={isOpen} onClose={handleClose} size='small' preventAutoClose>
            <Form form={form} onChange={handleFormChange}>
                <Popup.Header
                    title={messages.title}
                    actionsSize='small'
                    actions={
                        <Button type='submit' size='medium' color='blue80' title={messages.save}>
                            {messages.save}
                        </Button>
                    }
                />

                <Popup.Body>
                    <Flex justifyContent='space-between'>
                        <ToggleButton.Group
                            block
                            name={schedulerFormNames.tab}
                            size='small'
                            width={240}
                            value={schedulerType}
                            onChange={handleTypeChange}
                        >
                            <ToggleButton color='midnight80' value={ScheduleType.Day}>
                                {messages.day}
                            </ToggleButton>

                            <ToggleButton color='midnight80' value={ScheduleType.Month}>
                                {messages.month}
                            </ToggleButton>

                            <ToggleButton color='midnight80' value={ScheduleType.Year}>
                                {messages.year}
                            </ToggleButton>
                        </ToggleButton.Group>

                        <Button size='small' color='blue10' startIcon={<VisibilityOnIcon size={12} />}>
                            <Text font='label' fontSize='small' uppercase>
                                {messages.prediction}
                            </Text>
                        </Button>
                    </Flex>

                    <Spacing height={24} />

                    <Flex justifyContent='start' spacing='24'>
                        <TimePicker
                            name={schedulerFormNames.time}
                            size='small'
                            label={messages.time}
                            required
                            spacing='8'
                        />

                        <DatePicker
                            name={schedulerFormNames.endDate}
                            size='small'
                            label={messages.endDate}
                            view='month'
                            placeholder={messages.select}
                            spacing='8'
                        />
                    </Flex>

                    <Spacing height={24} />

                    <Flex justifyContent='start' spacing='16' direction='column'>
                        {{
                            [ScheduleType.Day]: <SchedulerDay />,
                            [ScheduleType.Month]: <SchedulerMonth />,
                            [ScheduleType.Year]: <SchedulerYear />,
                        }[schedulerType] ?? null}
                    </Flex>
                </Popup.Body>
            </Form>
        </Popup>
    );
});

SchedulerPopup.displayName = 'SchedulerPopup';
